import { memo, useMemo, useRef } from "react";
import { GifPreview } from "./GifPreview";
import { IGif, INicheFeedbackResponse } from "@/common/services/types";
import { usePlayerObserver } from "./hooks/usePlayerObserver";
import { TAddFeedback } from "components/gifPreview/Vote";
import { useAppStore } from "@/common/store/appStore";
import { clsx } from "clsx";
import { GifType } from "./constants";
import { useGifMetrics } from "./hooks/useGifMetrics";
import { useLikesData } from "./hooks/useLikesData";
import { useUsersStore } from "@/common/store/storeUsers";

interface IGifPreviewContainerProps {
  gif: IGif;
  embedded?: boolean;
  hideGif: (id: string) => void;
  rel?: string;
  position: number;
  addNicheFeedback?: TAddFeedback;
  nicheFeedbacks?: INicheFeedbackResponse[];
}

const GifPreviewContainerComponent = (props: IGifPreviewContainerProps) => {
  const { hideGif, position, rel, addNicheFeedback, nicheFeedbacks } = props;

  const gif = props.gif;
  const user = useUsersStore(state => state.getUser(gif.userName));

  const playerWrapRef = useRef<HTMLDivElement>(null);
  const isFullScreen = useAppStore((state) => state.isFullScreen);
  const { isVisible, isActive } = usePlayerObserver({ playerWrapRef, position, fullscreen: isFullScreen, gifId: gif.id });

  const { sendGifView, playCount, incrementPlayCount } = useGifMetrics({ position });

  useLikesData();

  const { height, isVertical } = useMemo(() => {
    const ratio = gif.width / gif.height;
    const height = ratio < 1.0 ? gif.height : gif.width;
    return { height, isVertical: ratio < 1.0 };
  }, [gif.width, gif.height]);

  return (
    <div
      id={`gif_${gif.id}`}
      className={
        clsx({
          "GifPreview": true,
          "GifPreview_isActive": isVisible,
          "GifPreview_isGallery": !!gif.gallery,
          "GifPreview_isImage": isActive && gif.type === GifType.Image,
          "GifPreview_isVideo": isActive && gif.type === GifType.Video,
          "GifPreview_isWithRepost": !!gif.repost && addNicheFeedback && nicheFeedbacks,
          "GifPreview_isVertical": isVertical
        })
      }
      style={{ aspectRatio: isFullScreen ? undefined : `${gif.width} / ${height}` }}
      ref={playerWrapRef}
    >
      { (isActive || isVisible) && (
        <GifPreview
          gif={gif}
          user={user}
          rel={rel}
          position={position}
          wrapRef={playerWrapRef}
          fullscreen={isFullScreen}
          isVisible={isVisible}
          addNicheFeedback={addNicheFeedback}
          nicheFeedbacks={nicheFeedbacks}
          hideGif={hideGif}
          sendGifView={sendGifView}
          playCount={playCount}
          incrementPlayCount={incrementPlayCount}
        />
      )}
    </div>
  );
};

export const GifPreviewContainer = memo(GifPreviewContainerComponent);
